// Typed client for the real-money equity (Alpaca live) endpoints:
//   /api/EquityLiveTrading/settings  — per-user live creds + kill switch
//   /api/EquityLiveTrading/enabled    — flip the per-user live kill switch
//   /api/EquityLiveTrading/account    — live broker account (proves connection)
// The API secret is masked on read; send a blank secret to keep the stored one.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface EquityLiveSettings {
  apiKey: string;
  apiSecretMasked: string;
  liveTradingEnabled: boolean;
  updatedAt: string | null;
  masterEnabled: boolean;
}

export interface UpdateEquityLiveSettings {
  apiKey?: string;
  apiSecret?: string;      // blank keeps the stored secret
  liveTradingEnabled?: boolean;
}

export interface EquityLiveAccount {
  accountNumber: string;
  status: string;
  equity: number | null;
  cash: number | null;
  buyingPower: number | null;
  daytradeCount: number;
  patternDayTrader: boolean;
  tradingBlocked: boolean;
  transfersBlocked: boolean;
  accountBlocked: boolean;
}

// Unwrap the API's {error} shape so users see the message, not a raw JSON blob.
async function readError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const body = JSON.parse(text);
    if (body?.error) return String(body.error);
  } catch { /* not JSON */ }
  return text || `Request failed (${res.status})`;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<T>;
}

export const equityLiveTradingService = {
  getSettings: () => getJson<EquityLiveSettings>('/api/EquityLiveTrading/settings'),

  saveSettings: async (req: UpdateEquityLiveSettings): Promise<EquityLiveSettings> => {
    const res = await apiFetch(`${API_BASE}/api/EquityLiveTrading/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<EquityLiveSettings>;
  },

  setEnabled: async (enabled: boolean): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/api/EquityLiveTrading/enabled?enabled=${enabled}`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error(await readError(res));
  },

  // Throws on 404 (no creds) / 5xx — the caller treats any failure as "not connected".
  getAccount: () => getJson<EquityLiveAccount>('/api/EquityLiveTrading/account'),
};
